/** セグメンテーションの境界操作とセグメント計算を担う純粋ロジック。 */

import {
	replaceAllLabelMatches as replaceAllLabelMatchesInLabels,
	replaceLabelMatch as replaceLabelMatchInLabels,
	type LabelMatch,
} from "./labelSearch";

/** 隣接する工程分解点との最小間隔 (秒)。潰れたセグメントでラベルがずれるのを防ぐ。 */
export const MIN_SEGMENT_DURATION = 0.1;

/**
 * 矢印キーで工程分解点を動かす幅 (秒)。30fps 換算の 1 フレームと 10 フレーム。
 * タイムラインの 1px が何秒かは表示幅で変わるため、ドラッグだけでは狭いディスプレイで
 * 目的の位置に置けない。キーボードでは表示幅によらず同じ精度で追い込めるようにする。
 */
export const BOUNDARY_NUDGE_SEC = 1 / 30;
export const BOUNDARY_NUDGE_COARSE_SEC = 10 / 30;

/** 矢印キーによる工程分解点の移動量 (秒)。対象外のキーなら null。 */
export function boundaryNudgeSeconds(key: string, coarse: boolean): number | null {
	const step = coarse ? BOUNDARY_NUDGE_COARSE_SEC : BOUNDARY_NUDGE_SEC;
	if (key === "ArrowLeft") return -step;
	if (key === "ArrowRight") return step;
	return null;
}

export interface SegmentationState {
	readonly videoId: string;
	readonly duration: number;
	readonly boundaries: readonly number[];
	readonly labels: readonly string[];
}

export interface SegmentRange {
	readonly start: number;
	readonly end: number;
}

export interface SegmentOutput {
	video_id: string;
	segments: {
		segment_id: string;
		start_sec: number;
		end_sec: number;
		label: string;
	}[];
}

export function createSegmentationState(videoId: string, duration: number): SegmentationState {
	return { videoId, duration, boundaries: [], labels: [] };
}

function syncLabels(labels: readonly string[], boundaries: readonly number[]): string[] {
	const count = boundaries.length === 0 ? 0 : boundaries.length + 1;
	if (labels.length === count) return [...labels];
	if (labels.length < count) {
		return [...labels, ...Array.from({ length: count - labels.length }, () => "")];
	}
	return labels.slice(0, count);
}

/** 指定時刻の境界を挿入する場合の boundaries 配列上の挿入位置 (0..boundaries.length) を返す。 */
export function findBoundaryInsertionIndex(boundaries: readonly number[], time: number): number {
	const nextIndex = boundaries.findIndex((boundary) => boundary > time);
	return nextIndex === -1 ? boundaries.length : nextIndex;
}

export function addBoundary(state: SegmentationState, time: number): SegmentationState {
	const clamped = Math.max(0, Math.min(time, state.duration));
	if (state.boundaries.includes(clamped)) return state;

	const insertionIndex = findBoundaryInsertionIndex(state.boundaries, clamped);
	const gapStart = insertionIndex === 0 ? 0 : (state.boundaries.at(insertionIndex - 1) ?? 0);
	const gapEnd =
		insertionIndex === state.boundaries.length
			? state.duration
			: (state.boundaries.at(insertionIndex) ?? state.duration);
	if (gapEnd - gapStart < MIN_SEGMENT_DURATION * 2) return state;

	const boundedTime = Math.max(
		gapStart + MIN_SEGMENT_DURATION,
		Math.min(clamped, gapEnd - MIN_SEGMENT_DURATION),
	);
	const boundaries = [
		...state.boundaries.slice(0, insertionIndex),
		boundedTime,
		...state.boundaries.slice(insertionIndex),
	];
	const currentLabels = syncLabels(state.labels, state.boundaries);
	const labels = [
		...currentLabels.slice(0, insertionIndex + 1),
		"",
		...currentLabels.slice(insertionIndex + 1),
	];
	return { ...state, boundaries, labels: syncLabels(labels, boundaries) };
}

export function removeBoundary(state: SegmentationState, index: number): SegmentationState {
	if (index < 0 || index >= state.boundaries.length) return state;
	const boundaries = state.boundaries.filter((_, i) => i !== index);
	const currentLabels = syncLabels(state.labels, state.boundaries);
	const labels = [...currentLabels.slice(0, index + 1), ...currentLabels.slice(index + 2)];
	return { ...state, boundaries, labels: syncLabels(labels, boundaries) };
}

/** 境界追加でセグメントが分割された際、分割位置以降のセグメント index を1つ後ろにずらす。 */
export function shiftSegmentIndexForInsert(index: number, insertionIndex: number): number {
	return index >= insertionIndex ? index + 1 : index;
}

/** 境界削除で2つのセグメントが統合された際のセグメント index を補正する (統合先は removedIndex)。 */
export function shiftSegmentIndexForRemove(index: number, removedIndex: number): number {
	if (index < removedIndex) return index;
	if (index === removedIndex || index === removedIndex + 1) return removedIndex;
	return index - 1;
}

export function moveBoundary(
	state: SegmentationState,
	index: number,
	newTime: number,
): SegmentationState {
	if (index < 0 || index >= state.boundaries.length) return state;
	const lower = index === 0 ? 0 : (state.boundaries.at(index - 1) ?? 0);
	const upper =
		index === state.boundaries.length - 1
			? state.duration
			: (state.boundaries.at(index + 1) ?? state.duration);
	const boundedTime =
		upper - lower < MIN_SEGMENT_DURATION * 2
			? (lower + upper) / 2
			: Math.max(lower + MIN_SEGMENT_DURATION, Math.min(newTime, upper - MIN_SEGMENT_DURATION));
	const updated = state.boundaries.map((boundary, i) => (i === index ? boundedTime : boundary));
	return { ...state, boundaries: updated };
}

export function setLabel(
	state: SegmentationState,
	segmentIndex: number,
	label: string,
): SegmentationState {
	if (segmentIndex < 0 || segmentIndex >= state.labels.length) return state;
	const next = [...state.labels];
	next[segmentIndex] = label;
	return { ...state, labels: next };
}

/** 検索一致 1 件を置換した SegmentationState を返す。 */
export function replaceLabelMatch(
	state: SegmentationState,
	match: LabelMatch,
	replacement: string,
): SegmentationState {
	return { ...state, labels: replaceLabelMatchInLabels(state.labels, match, replacement) };
}

/** 与えられた一致箇所をすべて置換した SegmentationState を返す。 */
export function replaceAllLabelMatches(
	state: SegmentationState,
	matches: readonly LabelMatch[],
	replacement: string,
): SegmentationState {
	return { ...state, labels: replaceAllLabelMatchesInLabels(state.labels, matches, replacement) };
}

export function computeSegments(state: SegmentationState): SegmentRange[] {
	if (state.boundaries.length === 0) return [];

	const points = [0, ...state.boundaries, state.duration];
	const segments: SegmentRange[] = [];

	for (let i = 0; i < points.length - 1; i++) {
		const start = points[i];
		const end = points[i + 1];
		if (end > start) {
			segments.push({ start, end });
		}
	}

	return segments;
}

/** segment_id は環境ファイル ID を接頭辞にする。同一ワークスペース内の別動画とダウンストリームの成果物名が衝突するため。 */
export function toSegmentJSON(state: SegmentationState, videoFileId: string): SegmentOutput {
	const ranges = computeSegments(state);
	return {
		video_id: state.videoId,
		segments: ranges.map((r, i) => ({
			segment_id: `${videoFileId}_seg_${String(i + 1).padStart(3, "0")}`,
			start_sec: r.start,
			end_sec: r.end,
			label: state.labels[i] ?? "",
		})),
	};
}
